'use client';
import { forwardRef, type ComponentProps } from 'react';
import Link from 'next/link';

import { BreadCrumb } from '@picsart/design-system/components/BreadCrumbs';

type BreadCrumbLinkProps = Omit<ComponentProps<typeof BreadCrumb>, 'tag'> & { href: string };

/**
 * A breadcrumb that is itself the link.
 *
 * `BreadCrumb` renders a `<button>` by default, so wrapping it in `<Link>` nested
 * two interactive controls — two focus stops for one destination, and invalid
 * HTML. `tag="link"` renders the crumb as the `<a>`, and `legacyBehavior` hands
 * that anchor Next's href, client navigation and prefetch.
 *
 * `size` arrives from `BreadCrumbs`' `cloneElement` on its direct child, so it
 * must reach `BreadCrumb` through here — under the old `<Link>` wrapper it
 * landed on the link and the crumb never got its size class.
 *
 * `legacyBehavior` is deprecated from Next 15: on upgrade, drive the anchor with
 * `router.push`/`router.prefetch` instead.
 */
const BreadCrumbLink = forwardRef<HTMLAnchorElement, BreadCrumbLinkProps>(({ href, ...props }, ref) => (
  <Link href={href} passHref legacyBehavior>
    <BreadCrumb tag="link" ref={ref} {...props} />
  </Link>
));

BreadCrumbLink.displayName = 'BreadCrumbLink';

export default BreadCrumbLink;
